import React from 'react'
import { ReactNode } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Box, Card, Typography } from '@mui/material'
import ArrowForwardIcon from '@mui/icons-material/ArrowForward'
import palette from '../../common/styles/palette'

export interface HomeActionCardProps {
  /** Ícone de contorno do topo do card; é renderizado em 32px na cor da marca. */
  icon: ReactNode
  title: string
  description: string
  /** Rota de destino. Sem `path` e sem `onClick` o card não é clicável. */
  path?: string
  /** Ação alternativa à navegação (ex.: abrir o PDF do guia rápido). */
  onClick?: () => void
  /** `brand` pinta o card de roxo — usado pelo card do Guia Rápido. */
  variant?: 'default' | 'brand'
}

/**
 * Card de ação da Home: ícone, título, descrição e a chamada "Acessar →".
 *
 * É a única implementação de card da Home — todos os perfis renderizam este
 * componente dentro de um `Grid` de duas colunas.
 */
export default function HomeActionCard({
  icon,
  title,
  description,
  path,
  onClick,
  variant = 'default',
}: HomeActionCardProps) {
  const navigate = useNavigate()
  const { t } = useTranslation()

  const isBrand = variant === 'brand'
  const isInteractive = Boolean(onClick) || Boolean(path)

  const handleClick = () => {
    if (onClick) {
      onClick()
      return
    }
    if (path) {
      navigate(path)
    }
  }

  const contentColor = isBrand ? palette.brandForeground : palette.brand

  return (
    <Card
      onClick={isInteractive ? handleClick : undefined}
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        p: 3,
        cursor: isInteractive ? 'pointer' : 'default',
        backgroundColor: isBrand ? palette.brand : palette.surface,
        borderColor: isBrand ? 'transparent' : palette.border,
        '&:hover': { boxShadow: palette.shadowMd },
      }}
    >
      <Box
        sx={{
          display: 'flex',
          mb: 1.5,
          color: contentColor,
          '& .MuiSvgIcon-root': { fontSize: 32 },
        }}
      >
        {icon}
      </Box>
      <Typography
        component="div"
        sx={{
          fontWeight: 600,
          fontSize: '1rem',
          color: isBrand ? palette.brandForeground : palette.brandDark,
        }}
      >
        {title}
      </Typography>
      <Typography
        sx={{
          mt: 0.5,
          fontSize: '0.875rem',
          color: isBrand ? palette.brandForeground : palette.textMuted,
          opacity: isBrand ? 0.85 : 1,
        }}
      >
        {description}
      </Typography>
      {isInteractive && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 0.5,
            mt: 'auto',
            pt: 2,
            fontSize: '0.875rem',
            color: contentColor,
          }}
        >
          {t('title_dialog_access')}
          <ArrowForwardIcon sx={{ fontSize: 16 }} />
        </Box>
      )}
    </Card>
  )
}
